/**
  * @class meleContainer
  * @description
  *
  * # Purpose
  * Implements `<mele-container>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-container></mele-container>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-container',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleElement from 'meleElement'

const PROPERTIES = _.extend({}, {
  datakey: { description: 'identifier of the data content; used to store key of object displayed within the container', type: 'stringornull' },
  items: { type: 'array', description: 'child items', noattribute: true },
  readonly: { type: 'boolean', description: 'If set, will make all child inputs read-only' },
  split: { type: 'string', description: 'if set, will add split attr to all child inputs, which do not have split set' },
  inputsplit: { type: 'string', description: 'if set, will add split attribute to child inputs with missing split' },
  inputwidth: { type: 'string', description: 'if set, will add width attribute to child inputs with missing width' },
  propagate: { type: 'boolean', description: 'If set, will propagate message from inputs to ancestor .container' },
  invalid: { type: 'boolean', description: 'true if any input on the form is invalid', readonly: true },
  changed: { type: 'boolean', description: 'true if any input on the form changed', readonly: true },
  conflict: { type: 'boolean', description: 'true if any editable input on the form has conflict', readonly: true },
  eventSubmit: { type: 'function', description: 'Called when the container is submitted; if not set, will publish mmFormSubmit message to parent module' },
  eventCancel: { type: 'function', description: 'Called when the container is requested for cancel; if not set, will do nothing' },
  eventClear: { type: 'function', description: 'Called when the container is requested for clear values; if not set, will clear values of all contained inputs' },
  eventUndo: { type: 'function', description: 'Called when the container is requested for undo value changes; if not set, will put back original values' },
  eventNew: { type: 'function', description: 'Called when the container is requested for new item; if not set, will publish mmFormNew message to parent module' },
  eventValidate: { type: 'function', description: "Called when some container's input subitem changed, should perform special validations" },
  eventStatusChanged: { type: 'function', description: 'Called when a status of the form has changed; if not set, will publish mmFormStatus message to parent module' },
  eventValueChanged: { type: 'function', description: "Called when a value of container's input subitem changed; if not set, will publish mmValueChanged message to parent module" },
}, meleElement._properties)

export default class meleContainer extends meleElement {
  /* CHILDREN METHODS */
  itemsStore(items) { this.setItems(items) }
  itemsRetrieve() { return this.getItems() }

  /**
  * @description
  * retrieves JSON definition of contained items
  * @param {string|string[]} [keys] - list of keys for the items to be retrieved
  */
  getItems(keys) {
    return this.getItemElements(keys).map(el=>el?el.js:null)
  }

  getCount() {
    const items = this.getItemElements()
    return items?.length || 0
  } // getCount

  getItemElements(keys) {
    if(!this._body) return []
    if(_.isNil(keys)) {
      return this._body.findAll('>*')
    }
    if(typeof (keys)==='string' || typeof (keys)==='number') {
      keys = [keys]
    }
    if(!Array.isArray(keys)) return []
    return keys.map(key=>{
      if(_.isNil(key)) return null
      const el = this._body.find('>@{'+_.toString(key)+'}')
      return el || null
    })
  } // getItemElements

  limitItems(limit) {
    if(!(limit>0)) return
    if(!this._body) return
    const elements = this.getItemElements()
    while(elements.length>limit) {
      let el = elements.pop()
      el.deleteElement()
      el = null
    }
  } // limitItems

  /**
  * @description
  * returns true if child item with given key exists, otherwise false
  * @param {string} key - key for the item to be found
  */
  isItem(key) {
    if(!this._body) return false
    if(_.isNil(key)) return false
    const el = this._body.find('>@{'+_.toString(key)+'}')
    return !!el
  } // isItem

  /**
  * @description
  * retrieves JSON definition of contained item with given key
  * @param {string} key - key for the item to be retrieved
  */
  getItem(key) {
    if(!this._body) return null
    if(_.isNil(key)) return null
    const el = this._body.find('>@{'+_.toString(key)+'}')
    return el ? el.js : null
  } // getItem

  /**
  * @description
  * retrieves item's element with given key
  * @param {string} key - key for the item to be retrieved
  */
  getItemElement(key) {
    if(!this._body) return null
    if(_.isNil(key)) return null
    const el = this._body.find('>@{'+_.toString(key)+'}')
    return el
  } // getItemElement

  /**
  * @description
  * retrieves first item's element
  */
  getFirstItemElement() {
    if(!this._body) return null
    return this._body.find('>*')
  } // getFirstItemElement

  /**
  * @description
  * transforms item definition to full definition as appropriate
  * used before appending a new item to fulfill missing properties by default values
  * in meleContainer does nothing, but can be overwritten in derived classes to do what's needed.
  * @param {object} item - key for the item to be retrieved
  */
  coerceItem(item) {
    return item
  }

  /**
  * @description
  * removes existing and set new items
  * @param {object[]} items - items definition
  */
  setItems(items) {
    this.deleteItems()
    this.appendItems(items)
  }

  /**
  * @description
  * removes items
  * @param {object|object[]|string|string[]} [items] - items to be deleted; may be individual key, list of keys, hash containing key property or list of such hashes
  */
  deleteItems(items) {
    if(!this._body) return
    if(_.isNil(items)) {
      this._body.deleteChildren()
      this.formStatusChanged()
      return
    }

    items = Array.isArray(items) ? items : [items]
    items.forEach(item=>{
      if(typeof (item)==='string' || typeof (item)==='number') {
        item = { key: _.toString(item) }
      }
      if(_.isObjectLike(item) && typeof (item.key)!=='undefined') {
        this._body.forAll('>@{'+_.toString(item.key)+'}', el=>el.deleteElement())
      }
    })
    this.formStatusChanged()
  }

  /**
  * @description
  * inserts items before given item (or at the beginning if before_item not specified)
  * item definitions will be transformed using coerceItems before inserted
  * @param {object|object[]} items - item(s) definition
  * @param {object} [before_item] - DOM node (not item definition !) to insert the items before
  */
  prependItems(items, before_item) {
    const as_array = Array.isArray(items)
    if(!this._body) return as_array ? [] : null
    if(as_array) {
      items = items.map(item=>this.coerceItem(item))
    }else {
      items = this.coerceItem(items)
    }
    const result = this._body.prependChildren(items, before_item)
    this.formStatusChanged()
    return result
  }

  /**
  * @description
  * inserts items after given item (or at the end if after_item not specified)
  * item definitions will be transformed using coerceItems before inserted
  * @param {object|object[]} items - item(s) definition
  * @param {object} [after_item] - DOM node (not item definition !) to insert the items after
  */
  appendItems(items, after_item) {
    const as_array = Array.isArray(items)
    if(!this._body) return as_array ? [] : null
    if(as_array) {
      items = items.map(item=>this.coerceItem(item))
    }else {
      items = this.coerceItem(items)
    }
    const formStatusChanged = this.formStatusChanged
    this.formStatusChanged = function() {}
    const result = this._body.appendChildren(items, after_item)
    this.formStatusChanged = formStatusChanged
    this.formStatusChanged()
    return result
  } // appendItems

  updateItems(items) {
    const as_array = Array.isArray(items)
    items = as_array ? items : [items]
    if(!this._body) return as_array ? [] : null
    items = items.map(item=>this.coerceItem(item))
    const result = []

    const fragment = document.createDocumentFragment()
    items.forEach(item=>{
      const existing = this._body.findAll('>@{'+_.toString(item.key)+'}')
      if(existing.length>0) {
        existing.forEach(el=>el.updateElementFromJS(item))
        result.push(existing[0])
      }else{
      // result.push(this._body.appendChildren(item)); // old slower version
        result.push(fragment._insertChild(item))
      }
    })
    this._body.appendChild(fragment)

    this.formStatusChanged()
    return as_array ? result : result[0]
  }

  changeItemKey(old_key, new_key) {
    old_key = _.toString(old_key)
    this._body.forAll('>@{'+old_key+'}', el=>el.key=new_key)
    const selected = this.selected
    if(selected.indexOf(old_key)>=0) {
      this.selected = selected.map(key=>key==old_key?new_key:key)
    }
  }

  /* WORKING WITH CHILDREN INPUTS */
  getValue(key) {
    key = _.toString(key)
    const el = this._body.find('.mele-input@{'+key+'}')
    return el ? el.value : null
  }

  getInputElements(return_disabled, return_readonly, return_hidden) {
    let query = '.mele-input'
    if(!return_hidden) query+=':not([hidden])'
    if(!return_disabled) query+=':not([disabled])'
    if(!return_readonly) query+=':not([readonly])'
    return this._body.findAll(query).filter(el=>!el.parentElement.closest('.mele-input'))
  } // getInputElements

  getValues(return_disabled, return_readonly, return_hidden) {
    const values = {}
    let query = '.mele-input'
    if(!return_hidden) query+=':not([hidden])'
    if(!return_disabled) query+=':not([disabled])'
    if(!return_readonly) query+=':not([readonly])'
    this._body.forAll(query, el=>{
      if(el.key) {
        values[el.key] = el.value
      }
    })
    return values
  }

  getVisibleValues() {
    const values = {}
    const query = '.mele-input'
    this._body.forAll(query, el=>{
      if(el.key) {
        values[el.key] = el.toText()
      }
    })
    return values
  }

  getChangedValues() {
    const values = {}
    const query = '.mele-input:not([hidden]):not([disabled]):not([readonly])[changed]'
    this._body.forAll(query, el=>{
      if(el.key) {
        values[el.key] = el.value
        // special tweak to return geoshape without name/address information as unnecessary for saving
        if(el.type=='geoshape') values[el.key] = el.value.replace(/\/.*/, '')
      }
    })
    return values
  }

  // update values for given keys, clearing not specified
  setValues(values, as_changed) {
    if(!_.isObjectLike(values)) values={}

    this.updateValues(values, as_changed)

    // delete not specified values
    this._body.forAll('.mele-input:not([compute])', el=>{
      if (Object.prototype.hasOwnProperty.call(values, el.key)) return
      el.value = ''
    })
    this.formStatusChanged()
  }

  // update values for given keys, keeping unchanged not specified
  updateValues(values, as_changed) {
    if(!_.isObjectLike(values)) return
    for(const key in values) {
      const value = values[key]
      this._body.forAll('.mele-input@{'+key+'}', el=>{
        if(as_changed) {
          const old = el.initialvalue
          el.value=value
          el.initialvalue=_.toString(old)
        }else{
          el.value=value
        }
      })
    }
    this.formStatusChanged()
  }

  // update server values for given keys
  setServerValues(values) {
    if(!_.isObjectLike(values)) values={}
    this.updateServerValues(values)

    // remove not specified server values
    this._body.forAll('.mele-input', el=>{
      if(Object.prototype.hasOwnProperty.call(values, el.key)) return
      el.servervalue = null
    })
    this.formStatusChanged()
  } // setServerValues

  // update values for given keys, keeping unchanged not specified
  updateServerValues(values) {
    if(!_.isObjectLike(values)) return
    for(const key in values) {
      const value = values[key]
      this._body.forAll('.mele-input@{'+key+'}', el=>{
        if(_.isNil(el.initialvalue)) {
          el.value = value
        }else {
          el.servervalue=value
        }
      })
    }
    this.formStatusChanged()
  } // updateServerValues

  // clear all initialvalue and servervalue properties
  commitValues() {
    this._body.forAll('.mele-input', el=>el.initialvalue=null)
    this.setServerValues()
  } // commitValues

  // focus on first editable input field
  focusFirst() {
    const first = this._body.find('>.mele-input:not([disabled]):not([readonly]) input,>.mele-input:not([disabled]):not([readonly]) textarea,>.mele-input:not([disabled]):not([readonly]) div[contenteditable]')
    if(first) first.focus()
  }

  // scroll to top
  scrollTop() {
    this.scrollTop = 0
  }

  scrollBottom() {
    this.scrollTop = this.scrollHeight
  }

  formStatusChanged() {
    let notify = false

    this.eventValidate()

    const inputs = this._body.findAll('.mele-input:not([hidden]):not([readonly]):not([disabled])')
    let changed = 0; let invalid = 0; let conflict = 0
    for(let i=0; i<inputs.length; i++) {
      const input = inputs[i]
      if(input.hasAttribute('changed')) changed++
      if(input.hasAttribute('invalid')) invalid++
      if(input.hasAttribute('conflict')) conflict++
      if(changed && invalid && conflict) break
    }
    changed = changed>0
    invalid = invalid>0
    conflict = conflict>0

    if(this.changed != changed) {
      this.changed = changed
      notify = true
    }

    if(this.invalid != invalid) {
      this.invalid = invalid
      notify = true
    }

    if(this.conflict != conflict) {
      this.conflict = conflict
      notify = true
    }

    // enable/disable specially deisgned buttons depending on form's change and validity status
    // we have to ensure this form is the closest to the button
    const buttons = this.querySelectorAll('.mele-button[action]')
    for(let i=0; i<buttons.length; i++) {
      const el = buttons[i]
      const holder = el.closest('.mele-container:not([propagate])')
      if(holder==this) {
        const action = el.getAttribute('action')
        if(action=='submit') el.disabled=!changed||invalid
        else if(action=='submitvalid') el.disabled=invalid
        else if(action=='undo') el.disabled=!changed
      }
    }

    if(this.readonly) {
      this.readonlyChanged(true)
    }

    if(this.split) {
      this.splitChanged(this.split)
    }

    if(notify) {
      this.eventStatusChanged(changed, invalid, conflict)
    }
  } // formStatusChanged

  formValueChanged(el, value, old, context_user_interaction) {
  // refresh headers/footers as they may contain references to changed value
    this.invokeDependentChanges('@');
    [...this.querySelectorAll('.mele-group[vars],.mele-input[prefix*="@{"],.mele-input[before*="@{"],.mele-input[after*="@{"],.mele-input[format*="@{"]')].forEach(el=>{
      try{
        el.invokeDependentChanges('@')
      }catch(e) {
      // do nothing
      }
    });
    [...this.querySelectorAll('.mele-tabs')].forEach(el=>{
      try{
        el.refreshTabs()
      }catch(e) {
      //do nothing
      }
    });

    [...this.querySelectorAll('[recompute]')].forEach(elComputed=>{
      if(elComputed!==el) return
      try{
        let calc = /^MELE-/i.test(elComputed.tagName) ? elComputed.recompute : elComputed.getAttribute('recompute')
        if(/^\w+\(\)$/.test(calc)) calc = elComputed[calc.replace(/\(\)$/, '')]
        if(Array.isArray(calc)) calc = calc.join('\n')
        const scripts = _.get(elComputed.closest('.mele-window,.mele-modal'), 'module.workflow.parsed_scripts', {})
        if(scripts[calc]) calc = scripts[calc]
        const fcn = typeof (calc)==='function' ? calc : new Function('holder', 'emitter', calc)
        const holder = elComputed.closest('.mele-container:not([propagate])')
        const WORKFLOW = scripts
        fcn.call(elComputed, holder, el)
      }catch(e) {
        // console.error('invalid recompute: '+e.message)
      }
    })

    const compute = [...this.querySelectorAll('[compute]')]
    if(this.compute) compute.push(this)
    compute.forEach(elComputed=>{
      if(elComputed===el) return
      try{
        let calc = /^MELE-/i.test(elComputed.tagName) ? elComputed.compute : elComputed.getAttribute('compute')
        if(/^\w+\(\)$/.test(calc)) calc = elComputed[calc.replace(/\(\)$/, '')]
        if(Array.isArray(calc)) calc = calc.join('\n')
        const scripts = _.get(elComputed.closest('.mele-window,.mele-modal'), 'module.workflow.parsed_scripts', {})
        if(scripts[calc]) calc = scripts[calc]
        const fcn = typeof (calc)==='function' ? calc : new Function('holder', 'emitter', calc)
        const holder = elComputed.closest('.mele-container:not([propagate])')
        const WORKFLOW = scripts
        fcn.call(elComputed, holder, el)
      }catch(e) {
        // console.error('invalid compute: '+e.message)
      }
    })

    // inform groups that show children according to some form field value
    this.forAll('[show]', el=>{
      try{
        el.showChanged(el.show)
      }catch(e) {}
    })

    // invoke notification event
    this.eventValueChanged(el, value, old, context_user_interaction)
  } // formValueChanged

  formSubmit(btn) {
    if(this.disabled) return
    this.eventSubmit(this, this.datakey, this.getValues(), btn)
  }

  formSubmitalways(btn) {
    this.eventSubmit(this, this.datakey, this.getValues(), btn)
  }

  formSubmitvalid(btn) {
    if(this.disabled) return
    this.eventSubmit(this, this.datakey, this.getValues(), btn)
  }

  formDelete() {
    if(this.disabled) return
    this.eventDelete(this, this.datakey)
  }

  formCancel() {
    this.eventCancel(this)
  }

  formClear() {
    if(this.disabled) return
    this.eventClear(this)
  }

  formUndo() {
    if(this.disabled) return
    this._body.forAll('>.mele-input', el=>el.undo())
    this.eventUndo(this)
  }

  formNew() {
    if(this.disabled) return
    this.eventNew(this)
  }

  formOcr() {
    if(this.disabled) return
    this.eventOcr(this)
  }

  formAlpr() {
    if(this.disabled) return
    this.eventAlpr(this)
  }

  eventSubmit(self, datakey, values, btn) {
    const params = { holder: self.key, datakey, values }
    btn = _.toString(btn ? btn.key : null)
    if(btn!='') params.button = btn
    this.publish('mmFormSubmit', params)
  }

  eventDelete(self, datakey) {
    this.publish('mmFormDelete', { holder: self.key, datakey })
  }

  eventCancel(self) {
    this.publish('mmFormCancel', { holder: self.key, datakey: self.datakey })
  }

  eventClear(self) {
    this.setValues()
  }

  eventUndo(self) {
  }

  eventNew(self) {
    this.publish('mmFormNew', { holder: self.key })
  }

  eventOcr(self) {
    this.publish('mmOcrNew', { holder: self.key })
  }

  eventAlpr(self) {
    this.publish('mmAlprNew', { holder: self.key })
  }

  readonlyChanged(value) {
    const readonly = this.readonly
    if(!this._body) return
    this._body.forAll('.mele-input', el=>{
      if(readonly && !el.readonly) {
        el.was_not_readonly = true
        el.readonly = true
      }else if(!readonly) {
        if(el.was_not_readonly) {
          el.readonly = false
        }
        delete el.was_not_readonly
      }
    })
  }

  splitChanged(value) {
    if(value) {
      if(!this._body) return
      this._body.forAll('>.mele-input:not([split])', el=>{
        if(el && !el.split) {
          el.split = value
        }
      })
    }
  }

  // invoked before form status change verification
  // used to implement custom multi-field validation
  eventValidate() {
  } // eventValidate

  // invoked when status of children changed
  eventStatusChanged(changed, invalid, conflict) {
    this.publish('mmFormStatus', { holder: this.key, changed, invalid, conflict })
  } // eventStatusChanged

  // called when child input value changed
  eventValueChanged(el, value, old, context_user_interaction) {
    const params = { holder: this.key, input: el.key, value, old }
    if(el.invalid) params.invalid = true
    if(context_user_interaction) params.interactive = true
    if(Array.isArray(params.value)) params.value = params.value.join('∫')
    if(Array.isArray(params.old)) params.old = params.old.join('∫')
    this.publish('mmValueChanged', params)
  } // eventValueChanged

  templateItems(template, return_disabled, return_readonly, return_hidden) {
    template = _.toString(template)
    if(template=='') return ''
    let query = '.mele-input'
    if(!return_disabled) query+=':not([disabled])'
    if(!return_readonly) query+=':not([readonly])'
    if(!return_hidden) query+=':not([hidden])'
    return this._body.findAll(query).filter(el=>!!el.key).map(el=>{
      return template
        .replace(/\$\{label\}/g, this.translate(el.label))
        .replace(/\$\{value\}/g, this.translate(el.toText()))
    }).join('')
  }

  simplify() {
    const repl = this.ownerDocument.createElement('div')
    repl.className = 'mele-container'
    this._body.forAll('>*', el=>{
      if(/^mele-/i.test(el.tagName)) el = el.simplify()
      repl.appendChild(el)
    })
    this.parentNode.replaceChild(repl, this)
    return repl
  }
} // class meleContainer

meleContainer._properties = PROPERTIES
meleContainer.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-container', meleContainer)
